import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PawPrint, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';

export default function PetsTermsPage() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
            <PawPrint className="w-5 h-5 text-[#1E3A5F]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--theme-text-primary)]">WANTD Pets — Terms & Conditions</h1>
            <p className="text-sm text-[var(--theme-text-muted)]">Effective date: April 2026 · Supplements the{' '}
              <Link to="/terms" className="text-[#1E3A5F] underline">General Terms of Service</Link>
            </p>
          </div>
        </div>

        <div className="space-y-8 text-[var(--theme-text-secondary)] text-sm leading-relaxed">

          {/* 1. Platform Role */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">1.</span> Platform Role — We Are a Facilitator
            </h2>
            <p>
              WANTD Pty Ltd (<strong>"WANTD"</strong>, <strong>"we"</strong>, <strong>"us"</strong>) operates an online
              marketplace that connects private individuals and registered breeders/rescue organisations with people
              seeking to adopt or purchase animals (<strong>"Pets Section"</strong>).
            </p>
            <p className="mt-2">
              WANTD is a <strong>facilitator only</strong>. We do not own, possess, transfer, sell, or take custody of
              any animal listed on the platform. All transactions are conducted directly between the listing party
              (<strong>"Seller/Rehomer"</strong>) and the adopting or purchasing party (<strong>"Adopter/Buyer"</strong>).
              WANTD is not a party to any agreement, sale, or transfer of animals made through the platform.
            </p>
            <div className="mt-3 p-3 rounded-lg bg-[#1E3A5F]/5 border border-[#1E3A5F]/20">
              <p className="text-xs text-[var(--theme-text-muted)]">
                <strong>ACL notice:</strong> Consumer guarantees under the Australian Consumer Law apply to transactions
                made by registered breeders and businesses. A one-off private transfer by an individual is generally
                not covered by consumer guarantees (the buyer has no statutory right to a return from a private seller).
                WANTD makes no representations about the accuracy of any listing.
              </p>
            </div>
          </section>

          {/* 2. Seller Obligations */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">2.</span> Seller / Rehomer Obligations
            </h2>
            <p className="mb-3">By listing an animal on WANTD Pets, you warrant and agree that:</p>
            <div className="space-y-2">
              {[
                'You are the legal owner (or authorised agent) of the animal you are listing.',
                'The animal is microchipped in accordance with applicable state or territory law before being listed. In most Australian states this is a legal requirement before an animal is sold or transferred.',
                'All information in your listing is accurate and not misleading, including the animal\'s breed, age, health status, vaccination records, and microchip number.',
                'The animal is at least 8 weeks of age before being listed for adoption or sale.',
                'You hold any registration or identification number required by your state or territory (see Section 3) and will provide it accurately in your listing.',
                'You will not ship, courier, freight, or transport animals across state or territory borders to facilitate a sale or adoption through this platform. Animals must be rehomed locally — the adopter must be able to view the animal in its current environment before transfer.',
                'You will not list any animal that has been subjected to cosmetic docking, ear cropping, or any other procedure prohibited under state animal welfare legislation.',
                'You agree to WANTD\'s right to remove any listing at any time and without notice if it does not comply with these terms.',
                'You indemnify WANTD against any claim, loss, liability, or expense (including legal costs) arising from your listing or from any inaccuracy, misrepresentation, or breach of applicable law by you.',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#059669] mt-0.5 flex-shrink-0" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 3. State Regulatory Requirements */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">3.</span> State & Territory Regulatory Requirements
            </h2>
            <p className="mb-3">
              The regulation of pet sales and transfers is a state and territory responsibility. Sellers must comply
              with all applicable requirements in their jurisdiction. The following mandatory identification numbers
              apply and <strong>must</strong> be included in any dog or cat listing:
            </p>
            <div className="space-y-3">
              <div className="p-3 rounded-lg border border-[var(--theme-border)] bg-[var(--theme-bg)]">
                <p className="font-medium text-[var(--theme-text-primary)] mb-1">New South Wales (from 1 December 2025)</p>
                <p>All dogs and cats listed for sale or transfer must include a valid <strong>Breeder Identification Number (BIN)</strong> or <strong>Rehoming Organisation Number (RON)</strong> issued by the NSW Pet Registry. Failure to include this number is an offence that may attract a fine of up to $16,500.</p>
                <a href="https://www.petregistry.olg.nsw.gov.au" target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E3A5F] underline mt-1 block">NSW Pet Registry →</a>
              </div>
              <div className="p-3 rounded-lg border border-[var(--theme-border)] bg-[var(--theme-bg)]">
                <p className="font-medium text-[var(--theme-text-primary)] mb-1">Victoria</p>
                <p>Anyone advertising a dog or cat for sale or transfer in Victoria must hold a <strong>Pet Exchange Register (PER) Source Number</strong>. It is a legal obligation for websites to ensure this number appears in all dog and cat advertisements. Failure is an offence under the Domestic Animals Act 1994.</p>
                <a href="https://agriculture.vic.gov.au/livestock-and-animals/animal-welfare-victoria/domestic-animals-act/pet-exchange-register" target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E3A5F] underline mt-1 block">VIC Pet Exchange Register →</a>
              </div>
              <div className="p-3 rounded-lg border border-[var(--theme-border)] bg-[var(--theme-bg)]">
                <p className="font-medium text-[var(--theme-text-primary)] mb-1">Queensland</p>
                <p>All dogs born after 26 May 2017 that are sold, supplied, or advertised in Queensland must have their owner's <strong>QDBR Supply Number</strong> included in any advertisement. This applies whether the transaction is free or for value.</p>
                <a href="https://qdbr.dpi.qld.gov.au" target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E3A5F] underline mt-1 block">QLD Dog Breeder Register →</a>
              </div>
              <div className="p-3 rounded-lg border border-[var(--theme-border)] bg-[var(--theme-bg)]">
                <p className="font-medium text-[var(--theme-text-primary)] mb-1">All other states and territories</p>
                <p>Microchipping and local council registration requirements apply across all states and territories. Sellers are responsible for ensuring compliance with their applicable jurisdiction.</p>
              </div>
            </div>
          </section>

          {/* 4. Private Rehoming Limit */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">4.</span> Private Rehoming Limit
            </h2>
            <p>
              Private individuals (not registered breeders or rescue organisations) may post a maximum of{' '}
              <strong>2 private rehoming listings within any 12-month period</strong>. This limit exists to prevent
              commercial breeders from misrepresenting commercial activity as private rehoming. Listings that exceed
              this limit will be automatically declined.
            </p>
            <p className="mt-2">
              Private rehoming listings may include a nominal rehoming fee of up to <strong>$150</strong> to cover
              reasonable costs such as vet checks or vaccinations. Listings with a price above $150 must use the
              "Registered Breeder" listing type.
            </p>
          </section>

          {/* 5. Prohibited Listings */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">5.</span> Prohibited Listings
            </h2>
            <p className="mb-3">The following listings are prohibited and will be removed without notice:</p>
            <div className="space-y-1.5">
              {[
                'Threatened, endangered, or protected native wildlife (requires separate state and federal permits — CITES)',
                'Exotic animals requiring an import or possession permit under applicable state laws (e.g. certain reptiles, primates)',
                'Livestock or farm animals (cattle, sheep, pigs, goats, poultry) — these are regulated under separate agricultural legislation',
                'Animals that have been subject to tail docking, ear cropping, debarking, or other prohibited cosmetic procedures',
                'Animals under 8 weeks of age',
                'Listings that advertise the shipping, freighting, or interstate transport of animals as part of the transaction',
                'Listings that are deceptive, misrepresent the animal\'s health, breed, age, or status, or that are intended to defraud',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 6. WANTD's Rights */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">6.</span> WANTD's Rights and Platform Moderation
            </h2>
            <p>
              WANTD reserves the right to remove any listing from the Pets Section at any time, with or without notice,
              if WANTD reasonably believes the listing: violates these terms; does not comply with applicable law; may
              involve animal welfare concerns; or is otherwise harmful to users or to the reputation of the platform.
            </p>
            <p className="mt-2">
              WANTD does not warrant that all listings comply with applicable law. The platform publishes listings in
              good faith based on information provided by sellers. Sellers bear sole legal responsibility for the
              accuracy of their listings and for compliance with all state and federal animal welfare laws.
            </p>
          </section>

          {/* 7. Welfare Reporting */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">7.</span> Welfare Reporting
            </h2>
            <p>
              If you become aware of a listing on WANTD Pets that you believe involves animal welfare concerns, illegal
              activity, or misrepresentation, please report it using the "Report" function on the listing, or contact
              us at <a href="mailto:legal@wantd.au" className="text-[#1E3A5F] underline">legal@wantd.au</a>.
              We aim to review and respond to welfare reports within 12 working hours.
            </p>
            <p className="mt-2">
              To report animal welfare concerns to the relevant authority, please contact:
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>RSPCA Australia: <a href="https://www.rspca.org.au" target="_blank" rel="noopener noreferrer" className="text-[#1E3A5F] underline">rspca.org.au</a></li>
              <li>Your local state animal welfare authority</li>
            </ul>
          </section>

          {/* 8. Limitation of Liability */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">8.</span> Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by Australian law, WANTD's liability in connection with the Pets Section
              is limited to the greater of the fees paid by you (if any) or $100 AUD. WANTD is not liable for any loss,
              injury, illness, or death of any animal, or for any financial loss arising from a transaction facilitated
              through the platform. This limitation does not exclude liability for death or personal injury caused by
              WANTD's negligence, or any other liability that cannot be excluded by law.
            </p>
          </section>

          {/* 9. Governing Law */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">9.</span> Governing Law
            </h2>
            <p>
              These Pets Terms are governed by the laws of Victoria, Australia. The parties submit to the non-exclusive
              jurisdiction of the courts of Victoria. These terms supplement and do not replace WANTD's{' '}
              <Link to="/terms" className="text-[#1E3A5F] underline">General Terms of Service</Link> and{' '}
              <Link to="/privacy" className="text-[#1E3A5F] underline">Privacy Policy</Link>. In the event of a
              conflict, these Pets Terms prevail to the extent of the inconsistency for matters relating to the
              Pets Section.
            </p>
          </section>

          {/* 10. Contact */}
          <section>
            <h2 className="text-base font-semibold text-[var(--theme-text-primary)] mb-2 flex items-center gap-2">
              <span className="text-[#1E3A5F] font-bold">10.</span> Contact
            </h2>
            <p>
              Questions about these terms or welfare concerns:{' '}
              <a href="mailto:legal@wantd.au" className="text-[#1E3A5F] underline">legal@wantd.au</a>
            </p>
          </section>

          {/* Footer badge */}
          <div className="flex items-center gap-2 pt-4 border-t border-[var(--theme-border)]">
            <Shield className="w-4 h-4 text-[#059669]" />
            <p className="text-xs text-[var(--theme-text-muted)]">
              WANTD Pets listings align with the{' '}
              <a href="https://kb.rspca.org.au/knowledge-base/rspca-guidelines-for-the-online-advertising-of-pets/" target="_blank" rel="noopener noreferrer" className="text-[#1E3A5F] underline">
                RSPCA Guidelines for the Online Advertising of Pets
              </a>.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
